import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { FullPokemonCard } from '@/types/pokemon'

function formatAttackNames(card: FullPokemonCard): string {
  const attacks = card.cardData.attacks
  if (!attacks?.length) return '—'
  return attacks.map((a) => a.name).join(', ')
}

export type CardDatabaseCardsTableProps = {
  items: FullPokemonCard[]
}

export function CardDatabaseCardsTable({ items }: CardDatabaseCardsTableProps) {
  const [preview, setPreview] = useState<{ imageUrl: string; name: string } | null>(null)

  return (
    <div className="rounded-md border overflow-x-auto">
      <Dialog
        open={!!preview}
        onOpenChange={(open) => {
          if (!open) setPreview(null)
        }}
      >
        <DialogContent className="max-w-[min(100%-2rem,28rem)] sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="pr-6 text-left line-clamp-2">{preview?.name}</DialogTitle>
          </DialogHeader>
          {preview ? (
            <div className="flex justify-center rounded-md border bg-muted/20 p-2">
              <img src={preview.imageUrl} alt="" className="max-h-[min(85vh,720px)] w-full object-contain" />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[64px]">Image</TableHead>
            <TableHead className="w-[72px]">#</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="min-w-[140px] max-w-[280px]">Attacks</TableHead>
            <TableHead>Set</TableHead>
            <TableHead className="w-[100px]">Code</TableHead>
            <TableHead className="hidden md:table-cell">Series</TableHead>
            <TableHead className="w-[72px]">Loc.</TableHead>
            <TableHead className="hidden sm:table-cell">Alt. name</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center text-muted-foreground">
                No cards match this search.
              </TableCell>
            </TableRow>
          ) : (
            items.map((row) => {
              const attacksLine = formatAttackNames(row)
              return (
                <TableRow key={row.id}>
                  <TableCell className="w-[64px] p-1 align-middle">
                    {row.cardData.imageUrl ? (
                      <button
                        type="button"
                        onClick={() => setPreview({ imageUrl: row.cardData.imageUrl!, name: row.name })}
                        className="mx-auto block cursor-zoom-in rounded border-0 bg-transparent p-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none"
                        title="View larger"
                        aria-label={`View larger image: ${row.name}`}
                      >
                        <img
                          src={row.cardData.imageUrl}
                          alt=""
                          className="block h-16 w-12 object-contain rounded border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
                          loading="lazy"
                        />
                      </button>
                    ) : (
                      <span className="flex h-16 w-12 items-center justify-center rounded border border-dashed border-gray-300 text-[10px] text-muted-foreground dark:border-gray-600">
                        —
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs tabular-nums">{row.collectorNumber}</TableCell>
                  <TableCell className="max-w-[200px] truncate font-medium" title={row.name}>
                    {row.name}
                  </TableCell>
                  <TableCell className="max-w-[280px] text-sm text-muted-foreground whitespace-normal align-top" title={attacksLine === '—' ? undefined : attacksLine}>
                    {attacksLine}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm" title={row.set.name}>
                    {row.set.name}
                  </TableCell>
                  <TableCell className="font-mono text-xs" title={row.set.code}>
                    {row.set.code}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground text-sm max-w-[160px] truncate" title={row.set.series}>
                    {row.set.series}
                  </TableCell>
                  <TableCell className="font-mono text-xs uppercase">{row.set.locale}</TableCell>
                  <TableCell className="hidden sm:table-cell text-sm max-w-[140px] truncate text-muted-foreground" title={row.alternativeName ?? ''}>
                    {row.alternativeName ?? '—'}
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </div>
  )
}
